import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarImage,
  useToast,
} from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";
import Context from "../../Context";
import { getAdminOrderUrl } from "../../constants";
import {
  getFavoriteUsers,
  isFavoriteUser,
  toggleFavoriteUser,
  removeFavoriteUser,
} from "../../util/favoriteUtils";
import {
  Users as UsersIcon,
  Search,
  RefreshCw,
  Phone,
  Copy,
  Check,
  LogIn,
  UserCheck,
  ShieldAlert,
  ShieldCheck,
  CreditCard,
  Ticket,
  Store,
  MoreVertical,
  X,
  User,
  Calendar,
  MessageCircle,
  Star,
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────

const SEARCH_TYPES = [
  { value: "phone", label: "เบอร์โทรศัพท์", placeholder: "เช่น 0812345678" },
  { value: "id", label: "รหัสผู้ใช้ (User ID)", placeholder: "เช่น USR123456" },
  { value: "firstName", label: "ชื่อ", placeholder: "พิมพ์ชื่อผู้ใช้..." },
  { value: "lastName", label: "นามสกุล", placeholder: "พิมพ์นามสกุล..." },
  { value: "lineTitle", label: "ชื่อไลน์ (Line Display)", placeholder: "พิมพ์ชื่อไลน์..." },
  { value: "lineId", label: "ไอดีไลน์ (Line ID)", placeholder: "พิมพ์ Line ID..." },
  { value: "verifiedId", label: "สถานะยืนยันตัวตน", placeholder: "" },
];

const VERIFIED_OPTIONS = [
  { value: "verified", label: "ยืนยันบัตรประชาชนแล้ว" },
  { value: "pending", label: "รอยืนยันบัตรประชาชน" },
  { value: "none", label: "ยังไม่ยืนยันบัตรประชาชน" },
];

const PAGE_SIZE = 25;

const formatUserDate = (dateVal) => {
  if (!dateVal) return "-";
  const date = new Date(dateVal);
  if (isNaN(date.getTime())) return "-";
  return (
    date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }) + " น."
  );
};

// ── Search & Filter Bar ───────────────────────────────────────────────────────

const SearchBar = ({
  searchType,
  searchValue,
  loading,
  onTypeChange,
  onValueChange,
  onSearch,
  onReset,
}) => {
  const currentConfig = SEARCH_TYPES.find((t) => t.value === searchType);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <div className="bg-white p-4 border-b border-slate-200">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Type Selector */}
        <div className="w-48 sm:w-52">
          <select
            className="w-full h-10 px-3 text-sm font-semibold text-slate-700 bg-slate-50 border border-slate-300 rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer disabled:opacity-50"
            value={searchType}
            disabled={loading}
            onChange={(e) => onTypeChange(e.target.value)}
          >
            {SEARCH_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Search Value Input (Controlled Width, not stretching 100%) */}
        <div className="w-72 sm:w-96 max-w-full">
          {searchType === "verifiedId" ? (
            <select
              className="w-full h-10 px-3 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-md shadow-xs focus:outline-none focus:border-blue transition-all cursor-pointer disabled:opacity-50"
              value={searchValue}
              disabled={loading}
              onChange={(e) => onValueChange(e.target.value)}
            >
              {VERIFIED_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <div className="relative w-full">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-8 text-sm font-medium text-slate-800 bg-white border border-slate-300 rounded-md shadow-xs placeholder:text-slate-400 focus:outline-none focus:border-blue transition-all disabled:opacity-50"
                placeholder={currentConfig?.placeholder || "พิมพ์คำค้นหา..."}
                value={searchValue}
                disabled={loading}
                onChange={(e) => onValueChange(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => onValueChange("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Search & Reset Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={onSearch}
            className="h-10 px-5 bg-blue hover:bg-blue/90 text-white text-sm font-bold rounded-md shadow-xs flex items-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            <span>ค้นหา</span>
          </button>

          {searchValue && (
            <button
              type="button"
              disabled={loading}
              onClick={onReset}
              className="h-10 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-semibold rounded-md transition-all disabled:opacity-50 cursor-pointer"
            >
              ล้างค่า
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Copyable Text Component ───────────────────────────────────────────────────

const CopyableText = ({ text, label = "" }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!text || text === "-") return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({
      className: "bg-slate-800 text-white text-xs font-semibold py-2 px-3 rounded-md",
      title: `คัดลอก ${label || text} เรียบร้อยแล้ว`,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!text || text === "-") return <span className="text-slate-400">-</span>;

  return (
    <span
      onClick={handleCopy}
      title="คลิกเพื่อคัดลอก"
      className="inline-flex items-center gap-1 group cursor-pointer hover:text-blue transition-colors"
    >
      <span className="font-mono">{text}</span>
      {copied ? (
        <Check className="w-3 h-3 text-emerald-600" />
      ) : (
        <Copy className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </span>
  );
};

// ── FavoriteUsersBar Component ─────────────────────────────────────────────

const FavoriteUsersBar = () => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [favorites, setFavorites] = useState(() => getFavoriteUsers());
  const [loginLoadingId, setLoginLoadingId] = useState(null);

  useEffect(() => {
    const handler = () => setFavorites(getFavoriteUsers());
    window.addEventListener("favorite_users_updated", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("favorite_users_updated", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const handleRemove = (e, userId) => {
    e.stopPropagation();
    removeFavoriteUser(userId);
    toast({
      className: "bg-slate-800 text-white font-semibold py-2 px-3 rounded-md",
      title: "นำออกจากรายการโปรดเรียบร้อย",
    });
  };

  const handleLogin = async (e, userId) => {
    e.stopPropagation();
    setLoginLoadingId(userId);
    try {
      const result = await callApi("users-addAdminToken", { userId });
      if (result?.adminToken) {
        window.open(getAdminOrderUrl(result.adminToken), "_blank");
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: "ไม่สามารถสร้าง Admin Token ได้",
        });
      }
    } catch {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "เกิดข้อผิดพลาดในการเข้าสู่ระบบแทนลูกค้า",
      });
    } finally {
      setLoginLoadingId(null);
    }
  };

  if (!favorites || favorites.length === 0) return null;

  return (
    <div className="bg-white rounded-md p-3.5 shadow-xs border border-amber-200/80 bg-linear-to-r from-amber-50/40 via-white to-amber-50/20">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
          <h2 className="font-bold text-xs text-slate-800 tracking-tight">
            favorite <span className="text-amber-600">({favorites.length})</span>
          </h2>
        </div>
        <span className="text-[10px] text-slate-400 font-medium">บันทึกในเครื่องนี้ (localStorage)</span>
      </div>

      <div className="flex items-center gap-2.5 overflow-x-auto pb-1 pt-0.5">
        {favorites.map((fav) => (
          <div
            key={fav.id}
            onClick={() => window.open(`/user/${fav.id}`, "_blank")}
            className="flex items-center gap-2 p-2 bg-white hover:bg-slate-50 border border-slate-200 hover:border-amber-400/80 rounded-md shadow-2xs hover:shadow-xs transition-all cursor-pointer shrink-0 min-w-[210px] max-w-[270px] group"
          >
            <Avatar className="w-8 h-8 rounded-md border border-slate-200 text-xs font-bold text-slate-600 shrink-0">
              {fav.profileImage ? (
                <AvatarImage src={fav.profileImage} alt={fav.firstName} className="rounded-md object-cover" />
              ) : (
                <span>{(fav.firstName?.[0] || fav.phone?.[0] || "U").toUpperCase()}</span>
              )}
            </Avatar>

            <div className="flex flex-col min-w-0 flex-1">
              <span className="font-bold text-xs text-slate-800 truncate group-hover:text-blue transition-colors">
                {fav.firstName ? `${fav.firstName} ${fav.lastName || ""}` : fav.phone || fav.id}
              </span>
              <span className="text-[10px] text-slate-400 font-mono truncate">
                {fav.phone || `ID: ${fav.id}`}
              </span>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                title="เข้าสู่ระบบแทนลูกค้า"
                disabled={loginLoadingId === fav.id}
                onClick={(e) => handleLogin(e, fav.id)}
                className="p-1.5 bg-blue/10 hover:bg-blue text-blue hover:text-white rounded-md transition-all cursor-pointer"
              >
                {loginLoadingId === fav.id ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : (
                  <LogIn className="w-3 h-3" />
                )}
              </button>

              <button
                type="button"
                title="นำออกจากรายการโปรด"
                onClick={(e) => handleRemove(e, fav.id)}
                className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-md transition-all cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── UserRow ───────────────────────────────────────────────────────────────────

const UserRow = ({ user }) => {
  const { callApi } = Context();
  const { toast } = useToast();
  const [loginLoading, setLoginLoading] = useState(false);
  const [isFav, setIsFav] = useState(() => isFavoriteUser(user.id));

  useEffect(() => {
    const handler = () => setIsFav(isFavoriteUser(user.id));
    window.addEventListener("favorite_users_updated", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("favorite_users_updated", handler);
      window.removeEventListener("storage", handler);
    };
  }, [user.id]);

  const handleToggleFav = (e) => {
    e.stopPropagation();
    const nowFav = toggleFavoriteUser(user);
    setIsFav(nowFav);
    toast({
      className: nowFav ? "bg-amber-600 text-white font-semibold py-2 px-3 rounded-md" : "bg-slate-800 text-white font-semibold py-2 px-3 rounded-md",
      title: nowFav ? `⭐ เพิ่ม ${user.firstName || user.phone || user.id} ในรายการโปรดแล้ว` : `นำออกจากรายการโปรดแล้ว`,
    });
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const result = await callApi("users-addAdminToken", { userId: user.id });
      if (result?.adminToken) {
        window.open(getAdminOrderUrl(result.adminToken), "_blank");
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold",
          title: "ไม่สามารถสร้าง Admin Token ได้",
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white font-semibold",
        title: "เกิดข้อผิดพลาดในการเข้าสู่ระบบแทนลูกค้า",
        description: err?.message || String(err),
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleViewProfile = () => {
    window.open(`/user/${user.id}`, "_blank");
  };

  // Verification Badge Colors
  const isVerified = user.verifiedId === "verified";
  const isPendingVerify = user.verifiedId === "pending";

  const getInitials = () => {
    const f = user.firstName?.[0] || "";
    const l = user.lastName?.[0] || "";
    return (f + l).toUpperCase() || <User className="w-3.5 h-3.5 text-slate-400" />;
  };

  return (
    <TableRow className="hover:bg-slate-50/80 transition-colors">
      {/* Registration Date */}
      <TableCell className="text-slate-600 text-xs whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <span>{formatUserDate(user.createdAt || user.registDate)}</span>
        </div>
      </TableCell>

      {/* Verification Status */}
      <TableCell className="text-center">
        <div className="inline-flex items-center gap-1.5">
          {/* ID Card Verification */}
          {isVerified ? (
            <span
              title="ยืนยันบัตรประชาชนแล้ว"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
            >
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              บัตร ปชช.
            </span>
          ) : isPendingVerify ? (
            <span
              title="รอยืนยันบัตรประชาชน"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200"
            >
              <ShieldAlert className="w-3 h-3 text-amber-600 animate-pulse" />
              รอยืนยัน
            </span>
          ) : (
            <span
              title="ยังไม่ยืนยันบัตรประชาชน"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500 border border-slate-200"
            >
              ยังไม่ยืนยัน
            </span>
          )}

          {/* OTP Status */}
          {user.activated ? (
            <span
              title="ยืนยันเบอร์โทรศัพท์ (OTP) แล้ว"
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue/10 text-blue border border-blue/20"
            >
              OTP
            </span>
          ) : (
            <span
              title="ยังไม่ยืนยัน OTP"
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-400"
            >
              -
            </span>
          )}
        </div>
      </TableCell>

      {/* User ID */}
      <TableCell className="text-xs font-semibold text-slate-700 whitespace-nowrap">
        <CopyableText text={user.id} label="รหัสผู้ใช้" />
      </TableCell>

      {/* Full Name & Avatar with Favorite Star */}
      <TableCell>
        <div className="flex items-center gap-2.5 min-w-[170px]">
          <button
            type="button"
            onClick={handleToggleFav}
            title={isFav ? "นำออกจากรายการโปรด" : "เพิ่มเป็นลูกค้าคนโปรด"}
            className="p-1 rounded-md text-slate-300 hover:text-amber-500 hover:bg-amber-50 transition-all cursor-pointer shrink-0"
          >
            <Star className={`w-4 h-4 ${isFav ? "fill-amber-400 text-amber-500" : ""}`} />
          </button>

          <Avatar className="w-7 h-7 bg-slate-100 border border-slate-200 text-xs font-bold text-slate-600 flex items-center justify-center shrink-0">
            {user.profileImage ? (
              <AvatarImage src={user.profileImage} alt={user.firstName} />
            ) : (
              <span>{getInitials()}</span>
            )}
          </Avatar>
          <div className="flex flex-col">
            <span className="font-semibold text-xs text-slate-900 leading-tight">
              {user.firstName || "-"} {user.lastName || ""}
            </span>
            {user.role && user.role !== "user" && (
              <span className="text-[10px] font-semibold text-blue uppercase tracking-wider">
                {user.role}
              </span>
            )}
          </div>
        </div>
      </TableCell>

      {/* Phone Number */}
      <TableCell className="text-xs font-medium text-slate-700 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5 text-slate-400" />
          <CopyableText text={user.phone} label="เบอร์โทร" />
        </div>
      </TableCell>

      {/* Line Name */}
      <TableCell className="text-xs font-medium text-slate-700 max-w-[140px] truncate">
        {user.lineTitle ? (
          <div className="flex items-center gap-1.5 truncate" title={user.lineTitle}>
            <MessageCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="truncate">{user.lineTitle}</span>
          </div>
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </TableCell>

      {/* Line ID */}
      <TableCell className="text-xs font-medium text-slate-700 max-w-[140px] truncate">
        {user.lineId ? (
          <CopyableText text={user.lineId} label="Line ID" />
        ) : (
          <span className="text-slate-400">-</span>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell className="text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-1.5">
          {/* Login as User */}
          <button
            type="button"
            title="เข้าสู่ระบบแทนลูกค้า"
            disabled={loginLoading}
            onClick={handleLogin}
            className="h-8 px-2.5 bg-blue hover:bg-blue/90 text-white text-xs font-bold rounded-md shadow-xs flex items-center gap-1.5 transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {loginLoading ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <>
                <LogIn className="w-3.5 h-3.5" />
                <span>เข้าสู่ระบบ</span>
              </>
            )}
          </button>

          {/* View Profile */}
          <button
            type="button"
            title="ดูข้อมูลลูกค้า"
            onClick={handleViewProfile}
            className="h-8 px-2.5 bg-green hover:opacity-90 text-white text-xs font-bold rounded-md shadow-xs flex items-center gap-1.5 transition-all cursor-pointer whitespace-nowrap"
          >
            <UserCheck className="w-3.5 h-3.5" />
            <span>โปรไฟล์</span>
          </button>

          {/* Create Order Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              title="สร้างคำสั่งซื้อ"
              className="h-8 w-8 bg-[#FAB12F] hover:opacity-90 text-white text-xs font-bold rounded-md shadow-xs flex items-center justify-center transition-all cursor-pointer shrink-0"
            >
              <MoreVertical className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-48" align="end">
              <DropdownMenuLabel className="text-xs font-bold text-slate-700">
                สร้างคำสั่งซื้อให้ลูกค้า
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => window.open(`/orders/create/${user.id}`, "_blank")}
                className="cursor-pointer flex items-center justify-between text-xs py-2"
              >
                <span>คำสั่งซื้อสลาก</span>
                <CreditCard className="w-4 h-4 text-blue" />
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`/coupon/order/create/${user.id}`, "_blank")}
                className="cursor-pointer flex items-center justify-between text-xs py-2"
              >
                <span>คำสั่งซื้อคูปอง</span>
                <Ticket className="w-4 h-4 text-amber-500" />
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.open(`/product/order/create/${user.id}`, "_blank")}
                className="cursor-pointer flex items-center justify-between text-xs py-2"
              >
                <span>คำสั่งซื้อร้านค้า</span>
                <Store className="w-4 h-4 text-emerald-600" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
};

// ── DataTable ─────────────────────────────────────────────────────────────────

const DataTable = ({ users, loading, loadingMore, sentinelRef, onResetSearch }) => (
  <div className="overflow-x-auto w-full">
    <Table className="w-full">
      <TableHeader className="bg-slate-50 border-b border-slate-200">
        <TableRow>
          <TableHead className="font-bold text-xs text-slate-700">วัน-เวลาที่สมัคร</TableHead>
          <TableHead className="font-bold text-xs text-slate-700 text-center">ยืนยันตัวตน</TableHead>
          <TableHead className="font-bold text-xs text-slate-700">รหัสผู้ใช้</TableHead>
          <TableHead className="font-bold text-xs text-slate-700">ชื่อ-นามสกุล</TableHead>
          <TableHead className="font-bold text-xs text-slate-700">เบอร์โทรศัพท์</TableHead>
          <TableHead className="font-bold text-xs text-slate-700">ชื่อไลน์</TableHead>
          <TableHead className="font-bold text-xs text-slate-700">ไอดีไลน์</TableHead>
          <TableHead className="font-bold text-xs text-slate-700 text-right pr-4">จัดการ</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length > 0 ? (
          <>
            {users.map((user) => (
              <UserRow key={user.id || user.phone} user={user} />
            ))}
            <TableRow ref={sentinelRef} className="border-none">
              <TableCell colSpan={8} className="text-center py-4">
                {loadingMore && (
                  <div className="flex justify-center items-center gap-2 text-xs font-semibold text-slate-500">
                    <RefreshCw className="w-4 h-4 animate-spin text-blue" />
                    <span>กำลังโหลดข้อมูลเพิ่มเติม...</span>
                  </div>
                )}
              </TableCell>
            </TableRow>
          </>
        ) : (
          <TableRow>
            <TableCell colSpan={8} className="py-16 text-center">
              <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                  <UsersIcon className="w-6 h-6" />
                </div>
                <p className="font-bold text-sm text-slate-700">ไม่พบข้อมูลลูกค้า</p>
                <p className="text-xs text-slate-400">
                  ลองตรวจสอบคำค้นหา หรือเปลี่ยนเงื่อนไขการค้นหาใหม่อีกครั้ง
                </p>
                {onResetSearch && (
                  <button
                    type="button"
                    onClick={onResetSearch}
                    className="mt-2 text-xs font-bold text-blue hover:underline cursor-pointer"
                  >
                    ล้างการค้นหาทั้งหมด
                  </button>
                )}
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

// ── Users Page Component ──────────────────────────────────────────────────────

export const Users = () => {
  const { callApi } = Context();
  const [users, setUsers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastIdRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line
  }, []);

  // IntersectionObserver for infinite scrolling
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, users]);

  const buildQuery = (startAfter = "") => ({
    queries: searchValue ? [[searchType, searchValue]] : [],
    limit: PAGE_SIZE,
    ...(!searchValue ? { orderBy: ["createdAt", "desc"] } : {}),
    ...(startAfter ? { startAfter } : {}),
  });

  // Fresh search
  const getUsers = async () => {
    setLoading(true);
    lastIdRef.current = null;
    try {
      const result = await callApi("users-getAll", buildQuery(), {
        notShowLoading: true,
      });
      const sorted = (result || []).sort((a, b) => b.createdAt - a.createdAt);
      setUsers(sorted);
      setHasMore(sorted.length === PAGE_SIZE);
      lastIdRef.current = sorted.at(-1)?.id ?? null;
    } catch (err) {
      console.warn("Failed to get users:", err);
    } finally {
      setLoading(false);
    }
  };

  // Load next page
  const fetchMore = async () => {
    if (!lastIdRef.current) return;
    setLoadingMore(true);
    try {
      const result = await callApi(
        "users-getAll",
        buildQuery(lastIdRef.current),
        { notShowLoading: true }
      );
      const sorted = (result || []).sort((a, b) => b.createdAt - a.createdAt);
      setUsers((prev) => [...prev, ...sorted]);
      setHasMore(sorted.length === PAGE_SIZE);
      lastIdRef.current = sorted.at(-1)?.id ?? null;
    } catch (err) {
      console.warn("Failed to fetch more users:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleTypeChange = (type) => {
    setSearchType(type);
    if (type === "verifiedId") {
      setSearchValue("verified");
    } else {
      setSearchValue("");
    }
  };

  const handleResetSearch = () => {
    setSearchValue("");
    setSearchType("phone");
    setTimeout(() => {
      getUsers();
    }, 0);
  };

  // Quick Stats
  const stats = useMemo(() => {
    const verifiedCount = users.filter((u) => u.verifiedId === "verified").length;
    const otpCount = users.filter((u) => u.activated).length;
    const hasLineCount = users.filter((u) => u.lineId || u.lineTitle).length;
    return {
      total: users.length,
      verified: verifiedCount,
      otp: otpCount,
      hasLine: hasLineCount,
    };
  }, [users]);

  return (
    <div className="w-full flex flex-col gap-4 pb-12">
      {/* Header with quick refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Header
          header="Users"
          subHeader="ค้นหาและจัดการข้อมูลลูกค้า"
          icon={<UsersIcon className="text-blue w-6 h-6" />}
        />
      </div>

      {/* Pinned Favorite Users Quick Access Bar */}
      <FavoriteUsersBar />

      {/* Main Content Card */}
      <div className="relative bg-white w-full rounded-md shadow-sm border border-slate-200 overflow-hidden">
        <SearchBar
          searchType={searchType}
          searchValue={searchValue}
          loading={loading}
          onTypeChange={handleTypeChange}
          onValueChange={setSearchValue}
          onSearch={getUsers}
          onReset={handleResetSearch}
        />

        {loading ? (
          <div className="bg-white flex flex-col justify-center items-center w-full h-80 gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue" />
            <p className="text-xs font-semibold text-slate-400">กำลังค้นหาข้อมูลลูกค้า...</p>
          </div>
        ) : (
          <DataTable
            users={users}
            loading={loading}
            loadingMore={loadingMore}
            sentinelRef={sentinelRef}
            onResetSearch={searchValue ? handleResetSearch : null}
          />
        )}
      </div>
    </div>
  );
};
